import {
    getListViewState,
    getRowKeysFromRowIds,
    getSelectedTableView,
    isItemPartOperation,
    isMailListConversationView,
    getStore as getListViewStore,
} from 'owa-mail-list-store';
import type TableView from 'owa-mail-list-store/lib/store/schema/TableView';
import {
    lazyMoveItemsBasedOnNodeIds,
    lazyMoveMailListRows,
    lazyMoveMailListRowsToFocusedOrOther,
    lazyAlwaysMoveRowToFocusedOrOther,
    lazyMoveSingleItemFromItemId,
} from 'owa-mail-triage-action';
import getProjection from 'owa-popout-v2/lib/utils/getProjection';
import { getActionSourceFromCommandingViewMode } from 'owa-command-ribbon';
import { lazyOnSingleSelectionChanged } from 'owa-mail-mark-read-actions';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import { resetFocus } from 'owa-mail-focus-manager';
import { getRootFolderIdForGivenContext } from 'owa-mail-moveto-control/lib/helpers/moveToCommonUtils';
import {
    setFocusOnMoveToMenuSearchBox,
    setFocusOnCopyToMenuSearchBox,
} from 'owa-mail-moveto-control/lib/helpers/setFocusOnMoveToMenuSearchBox';
import { getMailboxInfoForFolderId, lazyFetchMoreFolders } from 'owa-folders';
import {
    createNewFolder,
    createNewGroupFolder,
} from 'owa-mail-movetofolder-view/lib/components/createNewFolderUtil';
import moveToStore from 'owa-mail-moveto-control/lib/store/store';
import { getGroupIdFromTableQuery, isGroupTableQuery } from 'owa-group-utils';
import { getGroupRootFolderId } from 'owa-groups-shared-folders-store';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type { MailboxInfo, MailboxType } from 'owa-client-types';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { onSearchTextChangedMutator } from 'owa-mail-moveto-control/lib/actions/onSearchTextChanged';
import { onViewAllMenuClicked } from 'owa-mail-moveto-control/lib/utils/onViewAllMenuClicked';
import { type LoadMoreFoldersActionSource } from 'owa-folders-constants';
import type { ClientItemId } from 'owa-client-ids';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import { errorThatWillCauseAlert } from 'owa-trace';
import closeProjection from 'owa-popout-v2/lib/actions/closeProjection';
import getProjectionViewState from 'owa-popout-v2/lib/selectors/getProjectionViewState';
import { getMailboxInfoForGroup } from 'owa-mailbox-info/lib/getMailboxInfoForGroup';
import { isSovereignCloudInfraEnabled } from 'owa-account-cloud';
import { lazyShowMoveToFolderDialogV9 } from 'owa-mail-folder-moveto-dialog';
import { MoveToFolderScenario } from 'owa-mail-moveto-control-types';
import { lazyCreateCopyItem, lazyCreateCopyItemFailedNotification } from 'owa-mail-file-support';
import type CreateItemResponse from 'owa-service/lib/contract/CreateItemResponse';
import getTabById from 'owa-tab-store/lib/utils/getTabById';
import { type SecondaryReadingPaneTabViewState } from 'owa-tab-store';
import { isMessageFileMailboxInfo } from 'owa-client-types/lib/MessageFileMailboxInfo';
import getEffectiveRowKeys from 'owa-mail-reading-pane-store/lib/utils/getEffectiveRowKeys';
import tryCloseSecondaryReadingPaneTab from './utils/tryCloseSecondaryReadingPaneTab';

export const onMoveToFolderClicked = (
    folderId: string,
    projectionTabId?: string,
    selectedRowKey?: string,
    itemId?: ClientItemId
): void => {
    const tableView = getSelectedTableView();
    let markAsReadAction = Promise.resolve();
    let selectedRowKeys: string[] = [];
    if (projectionTabId && selectedRowKey) {
        selectedRowKeys = [selectedRowKey];
    } else {
        selectedRowKeys = getEffectiveRowKeys(tableView);
    }

    // Mark as read item if user selected only one item, as reading pane
    // cannot show more than one item at a time if there are multiple items selected.
    if (selectedRowKeys.length === 1 && selectedRowKeys[0]) {
        markAsReadAction = lazyOnSingleSelectionChanged.importAndExecute(
            selectedRowKeys[0],
            tableView,
            true /* isUserNavigation */
        );
    }

    if (isGlobalFeatureEnabled('grp-loadFolders') && isGroupTableQuery(tableView?.tableQuery)) {
        // In groups, the folder IDs are present in REST format. Since we are using OWS API to perform Move To in Groups,
        // REST ID needs to be converted to OWS format.
        // Tracking Bug - Bug 131273: Remove OWS to REST ID and vice versa Id conversion logic once platform provides support.
        folderId = folderId.split('-').join('/').split('_').join('+');
    }

    markAsReadAction.then(() => {
        if (isItemPartOperation() && !projectionTabId) {
            lazyMoveItemsBasedOnNodeIds.import().then(moveItemsBasedOnNodeIds => {
                moveItemsBasedOnNodeIds(
                    getListViewState().expandedConversationViewState.selectedNodeIds,
                    folderId,
                    tableView.id,
                    getActionSourceFromCommandingViewMode()
                );
            });
        } else {
            if (projectionTabId && itemId) {
                // Windows notification case where the popOut window has selectedRowKey is empty
                lazyMoveSingleItemFromItemId.importAndExecute(itemId.Id, folderId).then(() => {
                    closeProjection(projectionTabId, false /* redirectToDeeplink */);
                });
            } else {
                lazyMoveMailListRows.importAndExecute(
                    selectedRowKeys,
                    folderId,
                    tableView.id,
                    getActionSourceFromCommandingViewMode()
                );
                tryCloseSecondaryReadingPaneTab();
            }
        }
    });
    resetFocus('ribbonMoveMenuDismissed');
};

export const onCopyToFolderClicked = (
    folderId: string,
    itemId?: ClientItemId,
    projectionTabId?: string
): void => {
    const clientItem = itemId ? getMailStore().items.get(itemId.Id) : undefined;

    if (!clientItem || !clientItem.MailboxInfo) {
        errorThatWillCauseAlert('MailboxInfo is empty');
    }

    if (itemId && folderId && clientItem) {
        // Check if the item is a local item
        const tabData = (getTabById(projectionTabId) as SecondaryReadingPaneTabViewState)?.data;
        const isCachedItem = !!tabData?.isFileActivation && !!tabData?.isCachedItem;
        const isMessageFileItem =
            isGlobalFeatureEnabled('mon-msg-local-parser') &&
            isMessageFileMailboxInfo(clientItem.MailboxInfo);

        // For MessageFile items, use module context mailboxInfo instead of the MessageFile mailboxInfo
        const mailboxInfoForCopy = isMessageFileItem
            ? getModuleContextMailboxInfo()
            : clientItem.MailboxInfo;

        // Copy/Move requires a parent folder id, so if the item is local or if the item doesn't have a parent folder id, we need to create the item
        if (isCachedItem || !isMessageFileItem || !clientItem?.ParentFolderId?.Id) {
            // Copy/Move requires a parent folder id, so if the item is local or if the item doesn't have a parent folder id, we need to create the item
            lazyCreateCopyItem
                .importAndExecute(clientItem, folderId, mailboxInfoForCopy)
                .then((res: CreateItemResponse) => {
                    if (res.ResponseMessages?.Items?.[0]) {
                        const itemInfoResponseMessage = res.ResponseMessages.Items[0];
                        if (itemInfoResponseMessage.ResponseClass === 'Success') {
                            clientItem.ItemId = itemInfoResponseMessage.Items[0].ItemId;
                        }
                    }
                })
                .catch(e => {
                    lazyCreateCopyItemFailedNotification.importAndExecute(e.networkError);
                })
                .finally(() => {
                    // closing the deeplink window after the item is copied to the folder because the window is empty after copy/move
                    if (projectionTabId) {
                        // close the projection tab
                        closeProjection(projectionTabId, false /* redirectToDeeplink */);
                    }
                });
        } else {
            lazyMoveSingleItemFromItemId.importAndExecute(itemId.Id, folderId).then(() => {
                // closing the deeplink window after the item is copied to the folder because the window is empty after copy/move
                if (projectionTabId) {
                    // close the projection tab
                    closeProjection(projectionTabId, false /* redirectToDeeplink */);
                }
            });
        }
    } else {
        errorThatWillCauseAlert('Folder id should always be set');
    }
};

export const onCreateNewFolderClicked = (
    folderName: string,
    projectionTabId?: string,
    selectedRowKey?: string,
    itemId?: ClientItemId
): void => {
    const tableView = getSelectedTableView();
    const userMailboxInfo = getModuleContextMailboxInfo();
    if (isGlobalFeatureEnabled('grp-loadFolders') && isGroupTableQuery(tableView?.tableQuery)) {
        const groupId = getGroupIdFromTableQuery(tableView?.tableQuery);
        if (groupId) {
            const rootFolderId = getGroupRootFolderId(groupId, userMailboxInfo);
            const mailboxInfo: MailboxInfo = isSovereignCloudInfraEnabled()
                ? getMailboxInfoForGroup(userMailboxInfo, groupId)
                : /* eslint-disable-next-line owa-custom-rules/require-mailbox-info-package -- (https://aka.ms/OWALintWiki)
                   * To create a MailboxInfo object deep import and call the getMailboxInfoFor* function for the type of MailboxInfo you are creating.
                   *	> MailboxInfo must be obtained from the owa-mailbox-info package. */
                  {
                      type: 'GroupMailbox',
                      userIdentity: userMailboxInfo?.userIdentity ?? '',
                      mailboxSmtpAddress: groupId,
                  };

            if (rootFolderId) {
                createNewGroupFolder(folderName, rootFolderId, mailboxInfo).then(folderId => {
                    if (folderId) {
                        onMoveToFolderClicked(folderId, projectionTabId, selectedRowKey, itemId);
                    }
                });
            }
        }
    } else {
        const { moveToMailboxType, moveToSharedFolderRootId } = moveToStore();

        // Get appropriate root folder id depending on the current moveToMailboxType.
        // Except 'SharedMailbox', all other mbxs have static rootFolderId. But sharedFolder root Id is stored in local store.
        const rootFolderId =
            moveToMailboxType === 'SharedMailbox'
                ? moveToSharedFolderRootId
                : getRootFolderIdForGivenContext(moveToMailboxType, userMailboxInfo);

        if (rootFolderId === undefined) {
            return;
        }

        const mailboxInfo = getMailboxInfoForFolderId(
            rootFolderId,
            false /* routeToAuxIfAuxArchive */
        );

        createNewFolder(
            folderName,
            rootFolderId,
            mailboxInfo,
            projectionTabId ? getProjection(projectionTabId)?.window : undefined
        ).then(folderId => {
            if (folderId) {
                onMoveToFolderClicked(folderId, projectionTabId, selectedRowKey, itemId);
            }
        });
    }

    // Dismiss menu
    resetFocus('RibbonNewFolderDismissed');
};

// Handle custom folder creation for integrated spam (extesnsibility feature), to create the
// custom folder under the correct root folder ie. the Inbox of the mailbox that the item is in
export const onCreateAndMoveCustomFolderForSpamAddin = (
    folderName: string,
    mailboxInfo: MailboxInfo
): void => {
    const { moveToSharedFolderRootId } = moveToStore();
    if (moveToSharedFolderRootId === undefined) {
        return;
    }

    // Create custom under the correct root folder
    createNewFolder(folderName, moveToSharedFolderRootId, mailboxInfo).then(folderId => {
        if (folderId) {
            onMoveToFolderClicked(folderId);
        }
    });
};

export const onMoveToFocusedOtherInboxClicked = (
    toViewFilter: FocusedViewFilter,
    projectionTabId?: string,
    selectedRowKey?: string,
    itemId?: ClientItemId
): void => {
    const tableView = getSelectedTableView();
    let selectedRowKeys: string[] = [];
    if (projectionTabId && selectedRowKey) {
        selectedRowKeys = [selectedRowKey];
    } else if (projectionTabId && selectedRowKey === '' && itemId) {
        // Windows notification case where the popOut window has selectedRowKey empty
        selectedRowKeys = getRowKeyForWindowsNotification(tableView, itemId);
    } else {
        selectedRowKeys = getEffectiveRowKeys(tableView);
    }

    lazyMoveMailListRowsToFocusedOrOther.importAndExecute(
        selectedRowKeys,
        tableView.id,
        toViewFilter,
        getActionSourceFromCommandingViewMode()
    );
    if (selectedRowKeys && projectionTabId) {
        // close the projection tab
        closeProjection(projectionTabId, false /* redirectToDeeplink */);
    }
    tryCloseSecondaryReadingPaneTab();
    resetFocus('ribbonMoveMenuFocusedOther');
};

export const onAlwaysMoveToFocusedOtherInboxClicked = (
    toViewFilter: FocusedViewFilter,
    projectionTabId?: string,
    selectedRowKey?: string,
    itemId?: ClientItemId
): void => {
    const tableView = getSelectedTableView();
    let selectedRowKeys: string[] = [];
    if (projectionTabId && selectedRowKey) {
        selectedRowKeys = [selectedRowKey];
    } else if (projectionTabId && selectedRowKey === '' && itemId) {
        // Windows notification case where the popOut window has selectedRowKey empty
        selectedRowKeys = getRowKeyForWindowsNotification(tableView, itemId);
    } else {
        selectedRowKeys = getEffectiveRowKeys(tableView);
    }

    lazyAlwaysMoveRowToFocusedOrOther.importAndExecute(
        selectedRowKeys,
        tableView,
        toViewFilter,
        getActionSourceFromCommandingViewMode()
    );

    if (selectedRowKeys && projectionTabId) {
        // close the projection tab
        closeProjection(projectionTabId, false /* redirectToDeeplink */);
    }
    tryCloseSecondaryReadingPaneTab();
    resetFocus('ribbonMoveMenuAlwaysFocusedOther');
};

export const onMoveToDifferentFolderClicked = (
    mailboxInfo: MailboxInfo,
    currentSearchText: string,
    newSearchText: string,
    mailboxType: MailboxType | undefined,
    mailboxSmtpAddress: string | undefined,
    isCopyTo?: boolean,
    projectionTabId?: string,
    selectedRowKey?: string,
    itemId?: ClientItemId
): void => {
    if (isGlobalFeatureEnabled('fp-movetofolder-dialog')) {
        let sourceFolderId;
        if (itemId) {
            sourceFolderId = getMailStore().items.get(itemId.Id)?.ParentFolderId?.Id;
        } else {
            sourceFolderId = getListViewStore().tableViews.get(
                getListViewState().selectedTableViewId
            )?.tableQuery.folderId;
        }
        lazyShowMoveToFolderDialogV9.importAndExecute({
            scenario: isCopyTo ? MoveToFolderScenario.CopyTo : MoveToFolderScenario.MoveTo,
            targetWindow: getProjectionViewState(projectionTabId ?? '')?.window ?? window,
            mailboxInfo,
            selectorProps: {
                sourceFolderId,
                supportedMailBoxTypes: ['UserMailbox', 'ArchiveMailbox', 'SharedMailbox'],
                onFolderClick: (
                    destinationFolderId: string,
                    _destinationMailboxInfo: MailboxInfo
                ) => {
                    isCopyTo
                        ? onCopyToFolderClicked(destinationFolderId, itemId, projectionTabId)
                        : onMoveToFolderClicked(
                              destinationFolderId,
                              projectionTabId,
                              selectedRowKey,
                              itemId
                          );
                },
                excludeSearchFolders: true,
                excludeNotesFolder: true,
                includeAllAccounts: true,
                loadMoreActionSource: 'Ribbon',
            },
        });
    } else {
        isCopyTo
            ? setFocusOnCopyToMenuSearchBox(getProjectionViewState(projectionTabId || '')?.window)
            : setFocusOnMoveToMenuSearchBox();

        onViewAllMenuClicked({
            ...mailboxInfo,
            type: mailboxType ?? mailboxInfo.type,
            mailboxSmtpAddress: mailboxSmtpAddress ?? '',
        });
    }

    onSearchTextChangedMutator(currentSearchText, newSearchText, projectionTabId ?? '');
};

export const onLoadMoreFoldersClicked = (mailboxInfo: MailboxInfo): void => {
    lazyFetchMoreFolders.import().then(fetchMoreFolders => fetchMoreFolders(mailboxInfo, 'Ribbon'));
};

function getRowKeyForWindowsNotification(tableView: TableView, itemId: ClientItemId) {
    let selectedRowKeys: string[] = [];
    if (isMailListConversationView(tableView) && itemId.Id) {
        const item = getMailStore().items.get(itemId.Id);
        const conversationId = item?.ConversationId?.Id;
        const rowKeys = conversationId ? getRowKeysFromRowIds([conversationId], tableView) : [];
        selectedRowKeys = rowKeys.length > 0 ? [rowKeys[0]] : []; // get lastest conversation row key
    } else {
        selectedRowKeys = getRowKeysFromRowIds([itemId.Id], tableView);
    }
    return selectedRowKeys;
}
